import { useState } from "react";
import {
  BookOpen, Radio, Link, Music, Mic2, MonitorPlay, DollarSign, Settings,
  Target, BarChart3, Globe, Star, ChevronRight, ChevronDown, CheckCircle,
  Headphones, Upload, Zap
} from "lucide-react";

const Section = ({ icon: Icon, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden" data-testid={`guide-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
      >
        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-purple-400" />
        </div>
        <span className="text-white font-semibold text-sm flex-1">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-white/30" /> : <ChevronRight className="w-4 h-4 text-white/30" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-3 text-white/60 text-sm leading-relaxed">{children}</div>}
    </div>
  );
};

const Step = ({ num, title, children }) => (
  <div className="flex gap-3">
    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
      <span className="text-purple-400 text-xs font-bold">{num}</span>
    </div>
    <div>
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-white/50 text-sm mt-1">{children}</p>
    </div>
  </div>
);

const Tip = ({ children }) => (
  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-4 py-3 text-amber-300/80 text-xs">
    <span className="font-bold text-amber-400">Pro Tip:</span> {children}
  </div>
);

// ─── Station Owner Guide ───
const OwnerGuide = ({ onTabChange }) => (
  <div className="space-y-3" data-testid="owner-guide">
    <Section icon={Zap} title="Getting Started" defaultOpen>
      <Step num="1" title="Create Your Account">
        Sign up at the home page and choose your plan — Starter, Pro, or Premium. Each tier unlocks more features like white-label players, custom domains, and advanced analytics.
      </Step>
      <Step num="2" title="Complete Your Profile">
        Go to <button onClick={() => onTabChange?.('settings')} className="text-purple-400 hover:underline">Settings</button> and fill in your station name, logo, tagline, and social links. This info powers your public pages and player branding.
      </Step>
      <Step num="3" title="Choose Your Plan">
        Not sure which plan? Start with Starter (free) to explore. Upgrade anytime from <button onClick={() => onTabChange?.('billing')} className="text-purple-400 hover:underline">Billing</button>.
      </Step>
      <Tip>Complete the onboarding checklist on your Overview tab to unlock all features quickly.</Tip>
    </Section>

    <Section icon={Link} title="Connect Your Stream (AzuraCast)">
      <Step num="1" title="Get Your AzuraCast API Key">
        Log into your AzuraCast panel. Go to <strong className="text-white/80">My Account → API Keys</strong> and create a new key with read access.
      </Step>
      <Step num="2" title="Enter Your Station Details">
        In <button onClick={() => onTabChange?.('station')} className="text-purple-400 hover:underline">My Channel</button>, paste your AzuraCast URL and API key. The platform will auto-detect your station ID.
      </Step>
      <Step num="3" title="Verify Connection">
        Check <button onClick={() => onTabChange?.('health')} className="text-purple-400 hover:underline">Station Health</button> to confirm your stream is connected and data is flowing.
      </Step>
      <Tip>If you're using your own Icecast/Shoutcast server, use the External Stream option in Settings instead.</Tip>
    </Section>

    <Section icon={Music} title="Upload & Manage Music">
      <Step num="1" title="Music Library">
        Go to <button onClick={() => onTabChange?.('library')} className="text-purple-400 hover:underline">Music Library</button> to browse your AzuraCast media. Files uploaded to AzuraCast automatically appear here.
      </Step>
      <Step num="2" title="AutoDJ">
        Your AzuraCast AutoDJ handles rotation automatically. Use playlists in your AzuraCast panel to organize tracks by genre, time slot, or rotation weight.
      </Step>
      <Step num="3" title="Track Submissions">
        Artists submit tracks via the Submit page. Review incoming submissions in <button onClick={() => onTabChange?.('incoming')} className="text-purple-400 hover:underline">Submissions</button> — approve, reject, or set targeting preferences in Settings.
      </Step>
    </Section>

    <Section icon={Mic2} title="Go Live (Broadcasting)">
      <Step num="1" title="Configure Broadcasting">
        Go to <button onClick={() => onTabChange?.('broadcasting')} className="text-purple-400 hover:underline">Broadcasting</button> for your mount point URL, port, and password.
      </Step>
      <Step num="2" title="Connect Your Software">
        Use any broadcasting software (BUTT, Mixxx, SAM Broadcaster, OBS) — paste your server URL and credentials.
      </Step>
      <Step num="3" title="Go Live">
        Hit Go Live from the dashboard or start streaming from your software. AutoDJ will automatically pause and resume when you connect/disconnect.
      </Step>
      <Tip>Schedule recurring shows in the <button onClick={() => onTabChange?.('shows')} className="text-purple-400 hover:underline">Shows</button> tab to let listeners know when you're on air.</Tip>
    </Section>

    <Section icon={MonitorPlay} title="Player & Embed Setup">
      <Step num="1" title="White Label Player">
        Configure your branded player in <button onClick={() => onTabChange?.('whitelabel')} className="text-purple-400 hover:underline">White Label</button> — colors, logo, features (chat, visualizer, video, weather, etc.).
      </Step>
      <Step num="2" title="Station Page">
        Build a public landing page in <button onClick={() => onTabChange?.('pagebuilder')} className="text-purple-400 hover:underline">Page Builder</button> with hero images, about section, and embedded player.
      </Step>
      <Step num="3" title="Embed Widgets">
        Get embed codes from <button onClick={() => onTabChange?.('embed')} className="text-purple-400 hover:underline">Embed Hub</button> or <button onClick={() => onTabChange?.('widgets')} className="text-purple-400 hover:underline">Standard Widget</button> to place your player on any website.
      </Step>
      <Tip>Use a custom domain from <button onClick={() => onTabChange?.('domains')} className="text-purple-400 hover:underline">Domains</button> for a fully branded experience.</Tip>
    </Section>

    <Section icon={DollarSign} title="Monetization">
      <Step num="1" title="Ad Bookings">
        Businesses can book ad slots on your station through the Advertise page. Manage incoming bookings from the Admin Panel.
      </Step>
      <Step num="2" title="Premium Submissions">
        Artists pay for featured placement and priority targeting. Revenue from submissions flows directly through your PayPal integration.
      </Step>
      <Step num="3" title="Affiliate Program">
        Earn commissions by referring new station owners. Share your unique link from <button onClick={() => onTabChange?.('affiliate')} className="text-purple-400 hover:underline">Affiliate</button>.
      </Step>
    </Section>

    <Section icon={Settings} title="Settings & Branding">
      <Step num="1" title="Station Branding">
        Upload your logo, set colors, and configure your tagline in Settings. These carry across your player, station page, and embeds.
      </Step>
      <Step num="2" title="Custom Domain">
        Point your own domain to your station page via <button onClick={() => onTabChange?.('domains')} className="text-purple-400 hover:underline">Domains</button> — available on Pro and Premium plans.
      </Step>
      <Step num="3" title="Submission Preferences">
        Control what genres, BPM ranges, and content types you accept in your Submission Targeting Preferences (found in Settings).
      </Step>
    </Section>
  </div>
);

// ─── Artist Guide ───
const ArtistGuide = ({ onTabChange }) => (
  <div className="space-y-3" data-testid="artist-guide">
    <Section icon={Upload} title="How to Submit Music" defaultOpen>
      <Step num="1" title="Go to the Submit Page">
        Click <strong className="text-white/80">Submit</strong> in the top navigation or visit <span className="text-purple-400">/submit</span>.
      </Step>
      <Step num="2" title="Choose Your Submission Type">
        Select whether you're submitting as an Artist, Promoter, Label, or Manager. Each type has slightly different fields.
      </Step>
      <Step num="3" title="Fill In Track Details">
        Enter your artist name, track title, genre, email, and a downloadable link (Google Drive, Dropbox, WeTransfer). Streaming-only links (Spotify) can't be used for airplay.
      </Step>
      <Step num="4" title="Select Your PRO">
        Choose your Performance Rights Organization (ASCAP, BMI, SESAC, or Rights-Cleared). This is required for royalty tracking.
      </Step>
      <Tip>Include a short message about your track — it helps station owners make faster decisions.</Tip>
    </Section>

    <Section icon={Target} title="Targeting System">
      <Step num="1" title="General Pool (Free)">
        Your track is automatically matched to stations based on genre, BPM, and content preferences. This is free and reaches all compatible stations.
      </Step>
      <Step num="2" title="Target Stations (Premium)">
        Pick specific stations to submit to directly. Your track gets priority placement in their review queue with a "Priority" badge.
      </Step>
      <Step num="3" title="BPM & Explicit Tags">
        Adding your track's BPM and marking explicit content helps the auto-matching system find the right stations for your music.
      </Step>
      <Tip>Targeting specific stations gives you priority review — your track appears at the top of their incoming queue.</Tip>
    </Section>

    <Section icon={BarChart3} title="Track Your Status">
      <Step num="1" title="Submission Status">
        After submitting, you'll receive email notifications when stations approve or review your track.
      </Step>
      <Step num="2" title="Status Types">
        <strong className="text-white/80">Pending</strong> — Under review. <strong className="text-white/80">Approved</strong> — Added to rotation. <strong className="text-white/80">Featured</strong> — Highlighted on the Discover page.
      </Step>
      <Step num="3" title="Airplay Tracking">
        Once approved, your track's airplay is automatically tracked. View your plays and listener reach from your Artist Profile.
      </Step>
    </Section>

    <Section icon={Globe} title="Exposure & Discovery">
      <Step num="1" title="Discover Page">
        Approved tracks appear on the public Discover page where listeners browse stations and trending music.
      </Step>
      <Step num="2" title="Featured Placement ($10)">
        Pay for featured status to get a badge, top placement, and priority review. Available during submission.
      </Step>
      <Step num="3" title="Multi-Station Reach">
        The targeting system sends your music to multiple stations simultaneously — one submission, maximum exposure.
      </Step>
    </Section>

    <Section icon={Star} title="Premium Submissions">
      <Step num="1" title="Featured Tier ($10 one-time)">
        Get priority review, a Featured badge on the Discover page, and top placement when approved.
      </Step>
      <Step num="2" title="Artist Membership ($19.99/mo)">
        Unlimited submissions, artist dashboard with airplay analytics, profile page, and priority support.
      </Step>
      <Step num="3" title="Station Targeting">
        Premium feature: select exactly which stations receive your track. Your submission gets a "Priority" badge in their queue.
      </Step>
      <Tip>The Featured tier is a one-time boost. For ongoing exposure, the Artist Membership gives you the most value.</Tip>
    </Section>
  </div>
);

const HowToGuideTab = ({ currentUser, onTabChange }) => {
  const isListener = currentUser?.plan === 'listener' || currentUser?.user_type === 'listener';
  const isAdmin = currentUser?.is_admin || currentUser?.plan === 'admin';
  const [view, setView] = useState(isListener ? 'artist' : 'owner');

  return (
    <div className="space-y-6" data-testid="how-to-guide-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">How-To Guide</h2>
            <p className="text-white/40 text-xs">Everything you need to get the most out of the platform</p>
          </div>
        </div>
      </div>

      {/* Tab Switcher — show both guides for admins/owners */}
      {(!isListener || isAdmin) && (
        <div className="flex gap-2 bg-white/[0.02] border border-white/10 rounded-xl p-1" data-testid="guide-switcher">
          <button
            onClick={() => setView('owner')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition ${
              view === 'owner' ? 'bg-purple-500/20 text-purple-400' : 'text-white/40 hover:text-white/60'
            }`}
            data-testid="guide-owner-btn"
          >
            <Headphones className="w-4 h-4" /> Station Owner Guide
          </button>
          <button
            onClick={() => setView('artist')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition ${
              view === 'artist' ? 'bg-purple-500/20 text-purple-400' : 'text-white/40 hover:text-white/60'
            }`}
            data-testid="guide-artist-btn"
          >
            <Music className="w-4 h-4" /> Artist Guide
          </button>
        </div>
      )}

      {/* Guide Content */}
      {view === 'owner' ? <OwnerGuide onTabChange={onTabChange} /> : <ArtistGuide onTabChange={onTabChange} />}

      {/* Chat Support CTA */}
      <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-2xl p-6 flex items-center justify-between" data-testid="guide-chat-cta">
        <div>
          <h3 className="text-white font-bold">Still need help?</h3>
          <p className="text-white/50 text-sm mt-1">Chat with our support team — we're here to help you succeed.</p>
        </div>
        <button
          onClick={() => {
            const chatBubble = document.querySelector('[data-testid="chat-bubble"]');
            if (chatBubble) chatBubble.click();
          }}
          className="bg-purple-500 hover:bg-purple-600 text-white font-medium px-6 py-3 rounded-xl transition flex items-center gap-2 text-sm flex-shrink-0"
          data-testid="guide-open-chat-btn"
        >
          Chat with Us Live
        </button>
      </div>
    </div>
  );
};

export default HowToGuideTab;
